import { t } from "i18next";
import { match } from "ts-pattern";
import { z } from "zod";

import {
  AdHocPlaceOfSafetySchema,
  CuratedPlaceOfSafetySchema,
} from "@thalamos/common";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import { yesOnlySchema } from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import { getSupportedIncidentTypeSelection } from "../../workflowLogic/helpers/getIncidentTypeSelection.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";
import { includeOnlyEmergencyDepartmentForMca } from "../common/placesOfSafetyFilter.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "placeOfSafety",
    type: "placeOfSafety",
    schema: z.union([CuratedPlaceOfSafetySchema, AdHocPlaceOfSafetySchema]),
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("recordPlaceOfSafety.choosePlaceOfSafety.title"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    filter: includeOnlyEmergencyDepartmentForMca,
  })
  .withFieldAlwaysVisible({
    fieldName: "confirmPlaceOfSafetyCorrect",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    label: tk("recordPlaceOfSafety.choosePlaceOfSafety.confirmCorrect"),
    requiredLabel: tk("recordPlaceOfSafety.choosePlaceOfSafety.confirmCorrect"),
    isEditable: ({ formValues }) => formValues.placeOfSafety !== undefined,
  })
  .withFieldConditionallyVisible({
    fieldName: "placeOfSafetyType",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "hbPos136" as const,
          "emergencyDepartment" as const,
          "custody" as const,
          "policeStation" as const,
          "privateHome" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.hbPos136",
        ),
        value: "hbPos136",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.emergencyDepartment",
        ),
        value: "emergencyDepartment",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.custody",
        ),
        value: "custody",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.policeStation",
        ),
        value: "policeStation",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.privateHome",
        ),
        value: "privateHome",
      },
    ],
    isVisible: ({ incidentWorkItem }) =>
      match(
        incidentWorkItem
          ? getSupportedIncidentTypeSelection({ incidentWorkItem })
              // .orTee((e) => {
              //   console.error("Unsupported incident type", e.prettifyError());
              // })
              .unwrapOr(undefined)
          : undefined,
      )
        .with("136", () => true)
        .with("mca", () => false)
        .with(undefined, () => true)
        .exhaustive(),
    label: tk("recordPlaceOfSafety.choosePlaceOfSafetyType.title"),
    requiredLabel: tk("recordPlaceOfSafety.choosePlaceOfSafetyType.required"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "publicPlaceLocationDescription",
    type: "banner",
    bannerType: "warning",
    isVisible: ({ formValues }) =>
      formValues.placeOfSafetyType?.value === "privateHome",
    body: tk(
      "recordPlaceOfSafety.choosePlaceOfSafetyType.publicPlaceLocationDescriptionBanner",
    ),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyTakingPersonToEmergencyDepartment",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "noHealthBasedPlaceOfSafetyAvailable" as const,
          "unreasonableDelayFindingHbpos" as const,
          "medicalAttentionNeeded" as const,
          "policeStationUsed" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk(
          "recordPlaceOfSafety.whyTakingPersonToEmergencyDepartment.options.noHealthBasedPlaceOfSafetyAvailable",
        ),
        value: "noHealthBasedPlaceOfSafetyAvailable",
      },
      {
        label: tk(
          "recordPlaceOfSafety.whyTakingPersonToEmergencyDepartment.options.unreasonableDelayFindingHbpos",
        ),
        value: "unreasonableDelayFindingHbpos",
      },
      {
        label: tk(
          "recordPlaceOfSafety.whyTakingPersonToEmergencyDepartment.options.medicalAttentionNeeded",
        ),
        value: "medicalAttentionNeeded",
      },
      {
        label: tk(
          "recordPlaceOfSafety.whyTakingPersonToEmergencyDepartment.options.policeStationUsed",
        ),
        value: "policeStationUsed",
      },
      {
        label: tk(
          "recordPlaceOfSafety.whyTakingPersonToEmergencyDepartment.options.otherPleaseAddDetails",
        ),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) =>
      formValues.placeOfSafetyType?.value === "emergencyDepartment",
    label: tk("recordPlaceOfSafety.whyTakingPersonToEmergencyDepartment.title"),
    requiredLabel: tk(
      "recordPlaceOfSafety.whyTakingPersonToEmergencyDepartment.required",
    ),
  })
  .withFieldConditionallyVisible({
    fieldName: "otherEmergencyDepartmentReasonDetails",
    type: "textbox",
    ...new FieldBuilder().withSchema(z.string().min(1).max(500)).build(),
    isVisible: ({ formValues }) =>
      formValues.whyTakingPersonToEmergencyDepartment?.value === "other",
    label: tk("recordPlaceOfSafety.otherEmergencyDepartmentReasonDetails"),
    requiredLabel: tk(
      "recordPlaceOfSafety.otherEmergencyDepartmentReasonDetailsRequired",
    ),
    multiline: true,
    rows: "5",
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "custodyActionBanner",
    type: "banner",
    bannerType: "warning",
    body: tk("recordPlaceOfSafety.choosePlaceOfSafetyType.warningBanner"),
    isVisible: ({ formValues }) =>
      match(formValues.placeOfSafetyType?.value)
        .with("custody", "policeStation", () => true)
        .with("hbPos136", "emergencyDepartment", "privateHome", () => false)
        .with(undefined, () => false)
        .exhaustive(),
  })
  .withPage(["placeOfSafety", "confirmPlaceOfSafetyCorrect"])
  .withPage([
    "placeOfSafetyType",
    "custodyActionBanner",
    "publicPlaceLocationDescription",
    "whyTakingPersonToEmergencyDepartment",
    "otherEmergencyDepartmentReasonDetails",
  ])
  .build({ t });

export type RecordPlaceOfSafetyFormData = ReturnType<
  typeof formSpecification
>["formType"];
